/*
 * START_MODULE_CONTRACT
 *   PURPOSE: Inline status label with 5 semantic color variants
 *   PROPS:   variant (default|success|error|warning|info) + all native span attributes
 *   STATE:   none
 *   DEPENDS: M-CN (cn utility)
 *   EMITS:   native span events via spread props
 *   ACCESSIBILITY: inherits text color for semantic meaning; combine with aria-label for icon-only use
 *   LINKS:   M-BDG
 * END_MODULE_CONTRACT
 *
 * CHANGE_SUMMARY
 *   [2026-06-12] Added GRACE MODULE_CONTRACT and semantic blocks — no behavior change
 *   [2026-06-24] Added transition-colors (150ms) so semantic status changes morph calmly
 */

import { forwardRef } from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/cn"

// --- VariantDefinitions ---
const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-sm px-2 py-0.5 font-body text-xs font-medium transition-colors duration-150",
  {
    variants: {
      variant: {
        default: "bg-surface-card-dark text-muted-strong",
        success: "bg-trading-up/15 text-trading-up",
        error: "bg-trading-down/15 text-trading-down",
        warning: "bg-primary/15 text-primary",
        info: "bg-info/15 text-info",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

// --- TypeDefinitions ---
interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

// --- BadgeComponent ---
const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => {
    return <span ref={ref} className={cn(badgeVariants({ variant, className }))} {...props} />
  },
)
Badge.displayName = "Badge"

export { Badge, badgeVariants }
export type { BadgeProps }
